import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';
import ThemeSwitcher from './ThemeSwitcher';
import BrandLogo from './BrandLogo';
import { usePermissions } from '../context/PermissionsContext';
import { SETTING_SECTIONS } from '../config/settings';
import { getVisibleSettingSections, permissionGranted } from '../config/permissions';

/* ── Sidebar icons (inline SVGs) ── */
const permissionMap = {
  dashboard: 'dashboard.view',
  employee: 'employees.view',
  employer: 'employers.view',
  orders: 'orders.view',
  payments: 'payments.view',
  complaints: 'complaints.view',
  blacklist: 'blacklist.view',
  reports: 'reports.view',
  activity: 'activity.view',
};

const icons = {
  dashboard: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 5a1 1 0 011-1h4a1 1 0 011 1v5a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zM14 13a1 1 0 011-1h4a1 1 0 011 1v6a1 1 0 01-1 1h-4a1 1 0 01-1-1v-6z" />
    </svg>
  ),
  employee: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
  ),
  employer: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
    </svg>
  ),
  orders: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
    </svg>
  ),
  payments: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
  ),
  complaints: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
    </svg>
  ),
  blacklist: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
    </svg>
  ),
  activity: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  reports: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  ),
  settings: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  ),
  users: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
    </svg>
  ),
  roles: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.39-2.823 1.07-4" />
    </svg>
  ),
  addresses: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  ),
  nationalities: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  jobTypes: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  ),
  paymentOptions: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
    </svg>
  ),
  shifts: (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
};

const SETTINGS_OPTION_PATHS = SETTING_SECTIONS.map((section) => section.path);
const USER_ADMIN_PATHS = ['/users', '/roles'];

function isSettingsOptionPath(pathname) {
  return SETTINGS_OPTION_PATHS.includes(pathname);
}

function isUsersAdminPath(pathname) {
  return USER_ADMIN_PATHS.includes(pathname);
}

function ChevronIcon({ expanded }) {
  return (
    <svg
      className={`w-4 h-4 shrink-0 nexus-transition ${expanded ? 'rotate-90' : ''}`}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
    </svg>
  );
}

function SidebarCollapseIcon({ collapsed }) {
  if (collapsed) {
    return (
      <svg
        className="nexus-sidebar-collapse-icon shrink-0"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2.25}
        aria-hidden="true"
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 3.75H6A2.25 2.25 0 003.75 6v12A2.25 2.25 0 006 21.75h3" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 8.25L17.25 12l-3.75 3.75" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 12H10.5" />
      </svg>
    );
  }

  return (
    <svg
      className="nexus-sidebar-collapse-icon shrink-0"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2.25}
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 3.75H6A2.25 2.25 0 003.75 6v12A2.25 2.25 0 006 21.75h3" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 8.25L11.25 12l3.75 3.75" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 12H18" />
    </svg>
  );
}

function SidebarToggleButton({ collapsed, onClick, className = "", title, ariaLabel }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`nexus-header-sidebar-toggle nexus-transition ${className}`.trim()}
      title={title}
      aria-label={ariaLabel}
      aria-expanded={!collapsed}
    >
      <SidebarCollapseIcon collapsed={collapsed} />
    </button>
  );
}

function getInitialSidebarCollapsed() {
  if (typeof window === "undefined") return false;

  const saved = localStorage.getItem("sidebarCollapsed");
  if (saved === "true") return true;
  if (saved === "false") return false;

  return window.innerWidth < 1280;
}

export default function Layout({ children }) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(getInitialSidebarCollapsed);
  const [expandedGroup, setExpandedGroup] = useState(() => {
    if (isSettingsOptionPath(location.pathname)) return 'settings';
    if (isUsersAdminPath(location.pathname)) return 'users';
    return null;
  });
  const { permissions } = usePermissions();
  const isAdmin = permissions.includes('__admin__');
  const myPerms = permissions;

  const menu = [
    { key: 'dashboard', path: '/' },
    { key: 'employee', path: '/employee' },
    { key: 'employer', path: '/employer' },
    { key: 'orders', path: '/orders' },
    { key: 'payments', path: '/payments' },
    { key: 'complaints', path: '/complaints' },
    { key: 'blacklist', path: '/blacklist' },
    { key: 'reports', path: '/reports' },
    { key: 'users', adminOnly: true, collapsibleGroup: 'users' },
    { key: 'settings', permissionGroup: 'settings', collapsibleGroup: 'settings' },
    { key: 'activity', path: '/activity' },
  ];

  const visibleSettingSections = getVisibleSettingSections(isAdmin ? ['__admin__'] : myPerms);
  const settingsSubmenu = visibleSettingSections.map((section) => ({
    path: section.path,
    labelKey: `menu.${section.menuKey}`,
    icon: icons[section.iconKey] || icons.settings,
  }));

  const usersSubmenu = [
    { path: '/users', labelKey: 'menu.users', icon: icons.users },
    { path: '/roles', labelKey: 'menu.roles', icon: icons.roles },
  ];

  useEffect(() => {
    if (isSettingsOptionPath(location.pathname)) {
      setExpandedGroup('settings');
    } else if (isUsersAdminPath(location.pathname)) {
      setExpandedGroup('users');
    }
  }, [location.pathname]);

  useEffect(() => {
    function handleResize() {
      const saved = localStorage.getItem("sidebarCollapsed");
      if (saved !== null) return;
      setCollapsed(window.innerWidth < 1280);
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  function toggleSidebarCollapsed() {
    setCollapsed((current) => {
      const next = !current;
      localStorage.setItem("sidebarCollapsed", String(next));
      return next;
    });
  }

  function canSeeMenuItem(item) {
    if (item.adminOnly) return isAdmin;
    if (item.permissionGroup === 'settings') {
      return isAdmin || getVisibleSettingSections(myPerms).length > 0;
    }
    if (item.key === 'activity') return isAdmin || permissionGranted(myPerms, 'activity.view');
    const requiredPerm = permissionMap[item.key];
    if (!requiredPerm) return true;
    if (isAdmin) return true;
    return permissionGranted(myPerms, requiredPerm);
  }

  function renderSubmenuLinks(submenu) {
    return submenu.map((subItem) => (
      <Link
        key={subItem.path}
        to={subItem.path}
        className={`nexus-sidebar-sub-link flex items-center gap-3 ml-4 px-3 py-2 rounded-xl text-sm font-medium nexus-transition border border-transparent ${
          location.pathname === subItem.path ? 'nexus-sidebar-sub-active' : ''
        }`}
      >
        <span className="shrink-0">{subItem.icon}</span>
        <span>{t(subItem.labelKey)}</span>
      </Link>
    ));
  }

  function renderCollapsibleGroup(item) {
    const groupKey = item.collapsibleGroup;
    const isSettingsGroup = groupKey === 'settings';
    const expanded = expandedGroup === groupKey;
    const submenu = isSettingsGroup ? settingsSubmenu : usersSubmenu;
    const isActive = isSettingsGroup ? isSettingsOptionPath(location.pathname) : isUsersAdminPath(location.pathname);
    const defaultPath = submenu[0]?.path || (isSettingsGroup ? '/settings/addresses' : '/users');
    const groupIcon = isSettingsGroup ? icons.settings : icons.users;
    const groupLabel = t(isSettingsGroup ? 'menu.settings' : 'menu.usersAndRoles');

    return (
      <div key={item.key}>
        <button
          type="button"
          onClick={() => {
            const next = expanded ? null : groupKey;
            setExpandedGroup(next);
            if (next && !isActive) {
              navigate(defaultPath);
            }
          }}
          className={`flex w-full items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium nexus-transition border ${
            isActive ? 'nexus-nav-active' : 'nexus-sidebar-link border-transparent'
          }`}
        >
          <span className="shrink-0">{groupIcon}</span>
          {!collapsed && (
            <>
              <span className="flex-1 text-left capitalize">{groupLabel}</span>
              <ChevronIcon expanded={expanded} />
            </>
          )}
        </button>

        {expanded && !collapsed && (
          <div className="mt-1 space-y-1">{renderSubmenuLinks(submenu)}</div>
        )}
      </div>
    );
  }

  return (
    <div className="nexus-app-shell flex h-screen min-h-0">

      {/* ── Sidebar (md+) ── */}
      <aside
        className={`hidden md:flex relative overflow-visible flex-col nexus-sidebar ${
          collapsed ? "w-20" : "w-60"
        } nexus-transition`}
      >
        {/* Brand */}
        <div className={`nexus-sidebar-header px-5 ${collapsed ? "justify-center" : ""}`}>
          <BrandLogo collapsed={collapsed} size="md" />
        </div>

        {/* Nav items */}
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {menu.map((item) => {
            if (!canSeeMenuItem(item)) return null;

            if (item.collapsibleGroup) {
              return renderCollapsibleGroup(item);
            }

            const isActive = location.pathname === item.path;

            return (
              <div key={item.path}>
                <Link
                  to={item.path}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium nexus-transition border ${
                    isActive ? 'nexus-nav-active' : 'nexus-sidebar-link border-transparent'
                  }`}
                >
                  <span className="shrink-0">{icons[item.key] || icons.dashboard}</span>
                  {!collapsed && <span className="capitalize">{t(`menu.${item.key}`)}</span>}
                </Link>
              </div>
            );
          })}
        </nav>
      </aside>

      {/* ── Main Content ── */}
      <div className="flex-1 flex flex-col min-w-0 min-h-0">

        {/* Header */}
        <HeaderBar
          menu={menu}
          location={location}
          isAdmin={isAdmin}
          settingsSubmenu={settingsSubmenu}
          usersSubmenu={usersSubmenu}
          canSeeMenuItem={canSeeMenuItem}
          sidebarCollapsed={collapsed}
          onToggleSidebar={toggleSidebarCollapsed}
        />

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto p-4 md:p-6">
          {children}
        </main>

        {/* Footer — compact bar matching content width */}
        <footer className="nexus-app-footer px-4 md:px-6 py-1.5">
            <div className="text-[11px] leading-tight text-nexus-text-secondary">
              <span className="font-semibold text-nexus-accent tracking-wider uppercase">ShaqoHage</span>
              <br />
              © {new Date().getFullYear()} ShaqoHage Intelligence Systems. All rights reserved.
            </div>
          </footer>
      </div>
    </div>
  );
}

/* ── Header bar ── */
function HeaderBar({
  menu,
  location,
  isAdmin = false,
  settingsSubmenu = [],
  usersSubmenu = [],
  canSeeMenuItem,
  sidebarCollapsed = false,
  onToggleSidebar,
}) {
  const { t } = useTranslation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileExpandedGroup, setMobileExpandedGroup] = useState(() => {
    if (isSettingsOptionPath(location.pathname)) return 'settings';
    if (isUsersAdminPath(location.pathname)) return 'users';
    return null;
  });

  useEffect(() => {
    if (isSettingsOptionPath(location.pathname)) {
      setMobileExpandedGroup('settings');
    } else if (isUsersAdminPath(location.pathname)) {
      setMobileExpandedGroup('users');
    }
  }, [location.pathname]);

  function renderMobileCollapsibleGroup(item) {
    const groupKey = item.collapsibleGroup;
    const isSettingsGroup = groupKey === 'settings';
    const expanded = mobileExpandedGroup === groupKey;
    const submenu = isSettingsGroup ? settingsSubmenu : usersSubmenu;
    const isActive = isSettingsGroup ? isSettingsOptionPath(location.pathname) : isUsersAdminPath(location.pathname);
    const groupIcon = isSettingsGroup ? icons.settings : icons.users;
    const groupLabel = t(isSettingsGroup ? 'menu.settings' : 'menu.usersAndRoles');

    return (
      <li key={item.key}>
        <button
          type="button"
          onClick={() => setMobileExpandedGroup(expanded ? null : groupKey)}
          className={`flex w-full items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium nexus-transition border ${
            isActive
              ? 'nexus-nav-active'
              : 'text-nexus-text-secondary hover:bg-nexus-hover hover:text-nexus-text border-transparent'
          }`}
        >
          <span>{groupIcon}</span>
          <span className="flex-1 text-left capitalize">{groupLabel}</span>
          <ChevronIcon expanded={expanded} />
        </button>
        {expanded && (
          <ul className="mt-1 space-y-1">
            {submenu.map((subItem) => (
              <li key={subItem.path}>
                <Link
                  to={subItem.path}
                  onClick={() => setMobileOpen(false)}
                  className={`flex items-center gap-3 ml-4 px-3 py-2 rounded-xl text-sm nexus-transition ${
                    location.pathname === subItem.path
                      ? 'nexus-nav-active'
                      : 'text-nexus-text-secondary hover:bg-nexus-hover hover:text-nexus-text'
                  }`}
                >
                  <span>{subItem.icon}</span>
                  <span>{t(subItem.labelKey)}</span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </li>
    );
  }

  function getLoggedInName() {
    if (typeof window === 'undefined') return '';
    // 1) check explicit name keys
    const nameKey = localStorage.getItem('name') || localStorage.getItem('username');
    if (nameKey && !String(nameKey).includes('@')) return nameKey;

    // 2) check `user` which may be a JSON string
    const rawUser = localStorage.getItem('user');
    if (rawUser) {
      try {
        const parsed = JSON.parse(rawUser);
        const candidate = parsed?.name || parsed?.fullName || parsed?.displayName || parsed?.username;
        if (candidate && !String(candidate).includes('@')) return candidate;
      } catch (e) {
        // not JSON, fall through — if it's a plain string that's not an email, use it
        if (!String(rawUser).includes('@')) return rawUser;
      }
    }

    // 3) try to decode token payload
    const token = localStorage.getItem('token');
    if (!token) return '';

    try {
      const parts = token.split('.');
      if (parts.length >= 2) {
        let payload = parts[1];
        payload = payload.replace(/-/g, '+').replace(/_/g, '/');
        while (payload.length % 4) payload += '=';
        const decoded = atob(payload);
        const json = JSON.parse(decodeURIComponent(escape(decoded)));
        const candidate = json.name || json.given_name || json.preferred_username || json.username || json.sub;
        if (candidate && !String(candidate).includes('@')) return candidate;
        // if only email exists, use local-part before @ as a fallback
        const email = json.email || json.upn;
        if (email && String(email).includes('@')) {
          const local = String(email).split('@')[0];
          return local.charAt(0).toUpperCase() + local.slice(1);
        }
      }
    } catch (e) {
      // ignore
    }

    return '';
  }

  function getUserRole() {
    try {
      const rawUser = localStorage.getItem('user');
      if (rawUser) {
        const parsed = JSON.parse(rawUser);
        if (parsed?.role) return parsed.role;
        if (Array.isArray(parsed?.roles) && parsed.roles.length) return parsed.roles[0];
      }
      return localStorage.getItem('role') || '';
    } catch (e) {
      return '';
    }
  }

  const userName = getLoggedInName() || 'User';
  const userRole = getUserRole();

  return (
    <>
      <header className="nexus-header-bar sticky top-0 z-30 px-4 md:px-6 flex items-center justify-between gap-3">
        {/* Left: sidebar toggle (desktop) + mobile menu */}
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <SidebarToggleButton
            collapsed={sidebarCollapsed}
            onClick={onToggleSidebar}
            title={sidebarCollapsed ? t("layout.expand") : t("layout.collapse")}
            ariaLabel={sidebarCollapsed ? t("layout.expand") : t("layout.collapse")}
          />

          <button
            type="button"
            onClick={() => setMobileOpen((s) => !s)}
            className="md:hidden inline-flex items-center justify-center rounded-xl p-2.5 text-nexus-text-secondary hover:bg-nexus-hover hover:text-nexus-text border border-nexus-border"
            aria-label="Toggle menu"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>

        </div>

        {/* Right: lang + user */}
        <div className="flex items-center gap-3">
          <ThemeSwitcher />
          <LanguageSwitcher />

          {/* Notification bell */}
          <button className="relative p-2 rounded-lg text-nexus-text-secondary hover:bg-nexus-hover hover:text-nexus-text nexus-transition">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
          </button>

          {/* User avatar + info */}
          <div className="flex items-center gap-3">
            <div className="hidden md:block text-right">
              <p className="text-sm font-semibold text-nexus-text leading-tight">{userName}</p>
              {userRole && <p className="text-xs text-nexus-accent font-medium uppercase">{userRole}</p>}
            </div>
            <UserMenu isAdmin={isAdmin} />
          </div>
        </div>
      </header>

      {/* Mobile dropdown */}
      {mobileOpen && (
        <div className="absolute left-3 top-16 z-40 w-11/12 rounded-xl bg-nexus-surface border border-nexus-border p-3 shadow-2xl md:hidden">
          <ul className="space-y-1">
            {menu.map((item) => {
              if (!canSeeMenuItem(item)) return null;

              if (item.collapsibleGroup) {
                return renderMobileCollapsibleGroup(item);
              }

              return (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    onClick={() => setMobileOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium nexus-transition border ${
                      location.pathname === item.path
                        ? 'nexus-nav-active'
                        : 'text-nexus-text-secondary hover:bg-nexus-hover hover:text-nexus-text border-transparent'
                    }`}
                  >
                    <span>{icons[item.key] || icons.dashboard}</span>
                    <span className="capitalize">{t(`menu.${item.key}`)}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </>
  );
}

/* ── User menu dropdown ── */
function UserMenu({ isAdmin }) {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('role');
    localStorage.removeItem('roles');
    localStorage.removeItem('permissions');
    window.location.href = '/login';
  };

  if (!token) {
    return (
      <Link to="/login" className="text-sm text-white bg-nexus-accent px-4 py-1.5 rounded-xl font-semibold hover:bg-nexus-accent-light nexus-transition">
        {t('header.login')}
      </Link>
    );
  }

  // when logged in show dropdown
  return (
    <div className="relative">
      <button
        onClick={() => setOpen(s => !s)}
        className="flex items-center gap-2 p-1 rounded-xl hover:bg-nexus-hover nexus-transition"
      >
        <div className="h-9 w-9 rounded-xl bg-nexus-accent/10 border border-nexus-accent/25 flex items-center justify-center text-nexus-accent">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        </div>
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-44 bg-nexus-card border border-nexus-border rounded-xl shadow-2xl py-1 z-50">
          {isAdmin && (
            <Link
              to="/users"
              onClick={() => setOpen(false)}
              className="block px-4 py-2.5 text-sm nexus-user-menu-item nexus-transition"
            >
              {t('header.account')}
            </Link>
          )}
          <button
            onClick={handleLogout}
            className="w-full text-left px-4 py-2.5 text-sm nexus-user-menu-item nexus-user-menu-item--danger nexus-transition"
          >
            {t('header.logout')}
          </button>
        </div>
      )}
    </div>
  );
}
